import {Box} from 'src/design/atoms/Box';
import fileDownload from 'js-file-download';
import React, {useState} from 'react';
import type {
  GetPersonnelListRequestV2,
  UserTypeEnum} from 'src/api/ProactApiClient';
import {
  ModuleEnum,
  PermissionOperationEnum,
  UserStatusEnum
} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {ProjectMultiSelect} from 'src/app/databound-elements/ProjectMultiSelect';
import {Checkbox} from 'src/design/atoms/Checkbox';
import {SidebarLinkPersonnel} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useExportPersonnelMutation, useGetPersonnelListV2Query} from 'src/redux/queries/personnel';
import {useHasModulePermission} from 'src/shared/auth/useHasModulePermission';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {PersonnelDocumentTitle} from '../../../../../../app/personnel/personnelHelpers';
import {
  useAllUsersColumns,
  useAllUsersRowStyles,
} from './hooks/useAllUsersColumns';

export interface AllUsersListProps {
  onRowClick?: (id: number, userType: UserTypeEnum, vendorId?: number) => void;
}
export const AllUsersList: React.FC<AllUsersListProps> = ({onRowClick}) => {
  useSetDocumentTitle(PersonnelDocumentTitle);
  const {setAlert} = useAlert();

  const columns = useAllUsersColumns();
  const getRowStyles = useAllUsersRowStyles();

  const {hasPermission: hasExportPermission} = useHasModulePermission({
    module: ModuleEnum.Personnel,
    operation: PermissionOperationEnum.Export,
  });

  const [isPendingActionFilterChecked, setIsPendingActionFilterChecked] =
    useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);

  const handlePendingActionFilter = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const {checked} = e.target;
    setIsPendingActionFilterChecked(checked);
  };

  const tableState = useTableState({
    identifier: 'personnel-all-users',
    columns: columns,
    defaultColumnFilters: [
      {
        id: 'active',
        value: [UserStatusEnum.Active.toString()],
      },
    ],
    defaultColumnVisibility: {
      jobCode: false,
      'vendor.id': false,
    },
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'lastName', desc: false}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const request: GetPersonnelListRequestV2 = {
    start: start,
    length: length,
    filters: filters,
    filterFns: filterFns,
    sorting: sorting,
    hasPendingAction: isPendingActionFilterChecked,
    projectIds:
      selectedProjectIds.length > 0
        ? selectedProjectIds.map(id => parseInt(id, 10))
        : undefined,
  };
  const {data, isLoading, isError, isFetching, refetch} =
    useGetPersonnelListV2Query(request, {refetchOnMountOrArgChange: true});
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const [exportPersonnelMutation] = useExportPersonnelMutation();

  const exportPersonnel = async () => {
    const exportRequest: GetPersonnelListRequestV2 = {
      ...request,
      length: 1000000,
    };
    const response = await exportPersonnelMutation(exportRequest).unwrap();
    fileDownload(response!.data, `personnel_export.csv`);
    setAlert('Your file has been successfully exported. ', 'success');
  };

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      mantineTableBodyCellProps: ({row}) => ({
        style: getRowStyles(row.original),
      }),
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActions: ({row}) =>
        onRowClick && (
          <TableRowActions>
            <TableRowActions.View
              onClick={() =>
                onRowClick(
                  row.original.id,
                  row.original.userType,
                  row.original.vendorId
                )
              }
            />
          </TableRowActions>
        ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Filters>
            <Checkbox
              label="Has Pending Action"
              checked={isPendingActionFilterChecked}
              onChange={handlePendingActionFilter}
            />
            <Box w={{base: '100%', sm: 220}}>
              <ProjectMultiSelect
                size="sm"
                placeholder="Filter by Project"
                value={selectedProjectIds}
                onChange={setSelectedProjectIds}
              />
            </Box>
          </TableToolbar.Filters>
          {hasExportPermission && (
            <TableToolbar.Actions>
              <TableToolbar.Action variant="export" onClick={exportPersonnel} />
            </TableToolbar.Actions>
          )}
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <ContentWrapper>
      <PageHeader>
        <PageHeader.Title icon={SidebarLinkPersonnel.icon}>
          All Users
        </PageHeader.Title>
      </PageHeader>
      {table}
    </ContentWrapper>
  );
};
